import { useState, useEffect } from 'react';
import PostForm from './components/PostForm';
import PostList from './components/PostList';
import SearchTab from './components/SearchTab';
import ProfileEditor from './components/ProfileEditor';
import { profileService } from './services/profileService';
import { AnonymousProfile } from './types/Profile';
import { apiConfig } from './config/api';

// SEO 헬퍼 함수들
const updateMetaTag = (name: string, content: string, property?: boolean) => {
  const attribute = property ? 'property' : 'name';
  let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  
  meta.setAttribute('content', content);
};

const updateTitle = (title: string) => {
  document.title = title;
};

const updateCanonicalUrl = (url: string) => {
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  
  canonical.setAttribute('href', url);
};

export interface Post {
  id: number;
  user_id: string;
  display_name?: string;
  photo_url?: string;
  avatar_color?: string;
  image_url?: string;
  diary_text: string;
  posted_date: string;
  created_at: string;
  likes_count: number;
  liked?: boolean;
}

export interface Comment {
  id: number;
  user_id: string;
  display_name?: string;
  photo_url?: string;
  avatar_color?: string;
  content: string;
  created_at: string;
  parent_comment_id?: number;
  replies?: Comment[];
}

function App() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [canPost, setCanPost] = useState(true);
  const [activeTab, setActiveTab] = useState<'home' | 'search' | 'create' | 'activity' | 'profile'>('home');
  const [likedPosts, setLikedPosts] = useState<Post[]>([]);
  const [anonymousProfile, setAnonymousProfile] = useState<AnonymousProfile | null>(null);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [userTimezone, setUserTimezone] = useState<string>('');

  useEffect(() => {
    const initializeApp = async () => {
      // 1. 먼저 사용자 프로필 초기화
      initializeAnonymousProfile();
      
      // 2. 사용자 정보 가져오기
      await fetchUserInfo();
      
      // 3. 게시물 가져오기 (사용자 ID가 설정된 후)
      await fetchPosts();
      
      // 4. 일일 게시 확인
      await checkDailyPost();
    };
    
    initializeApp();
  }, []);

  const initializeAnonymousProfile = () => {
    let profile = profileService.getProfile();
    if (!profile) {
      profile = profileService.generateRandomProfile();
    }
    setAnonymousProfile(profile);
    
    // 사용자 시간대 정보 설정
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(timezone);
    
    // 사용자 ID 생성 및 쿠키 설정 (IP와 쿠키 조합)
    let userId = localStorage.getItem('user_id');
    if (!userId) {
      // IP 기반 해시 생성 (브라우저에서 가능한 범위)
      const userAgent = navigator.userAgent;
      const screenInfo = `${screen.width}x${screen.height}`;
      const ipHash = btoa(`${userAgent}_${screenInfo}_${timezone}`).replace(/[^a-zA-Z0-9]/g, '_').substring(0, 16);
      
      userId = `user_${ipHash}_${Date.now()}`;
      localStorage.setItem('user_id', userId);
    }
    
    // 쿠키 설정 (SameSite=None, Secure 옵션 추가)
    const cookieOptions = [
      `user_id=${userId}`,
      'path=/',
      'max-age=31536000', // 1년
      'SameSite=Lax',
      'HttpOnly=false' // JavaScript에서 접근 가능
    ].join('; ');
    
    document.cookie = cookieOptions;
  };

  const fetchUserInfo = async () => {
    try {
      const response = await fetch(apiConfig.endpoints.userInfo);
      const result = await response.json();
      if (result.code === 0) {
        setUserInfo(result.data);
      }
    } catch (error) {
      console.error('사용자 정보 조회 실패:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      // 사용자 ID 헤더 추가
      const userInfo = getEffectiveUserInfo();
      console.log('fetchPosts - 사용자 정보:', {
        userInfo,
        userId: userInfo?.id,
        hasUserId: !!userInfo?.id
      });
      
      if (userInfo?.id) {
        headers['X-Encrypted-Yw-ID'] = userInfo.id;
        headers['X-Is-Login'] = '1';
      }
      
      console.log('fetchPosts - 요청 헤더:', headers);
      
      const response = await fetch(apiConfig.endpoints.posts, { headers });
      if (response.ok) {
        const data = await response.json();
        console.log('fetchPosts - 받은 데이터:', {
          postsCount: data.length,
          postsWithLikes: data.filter(p => p.liked).length,
          samplePost: data[0] ? {
            id: data[0].id,
            liked: data[0].liked,
            likes_count: data[0].likes_count
          } : null
        });
        setPosts(data);
      } else {
        console.error('fetchPosts - 응답 실패:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('게시물 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkDailyPost = async () => {
    try {
      const response = await fetch(apiConfig.endpoints.checkDailyPost);
      if (response.ok) {
        const data = await response.json();
        setCanPost(data.canPost);
      }
    } catch (error) {
      console.error('일일 게시 확인 실패:', error);
    }
  };

  const fetchLikedPosts = async () => {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      // 사용자 ID 헤더 추가
      const userInfo = getEffectiveUserInfo();
      if (userInfo?.id) {
        headers['X-Encrypted-Yw-ID'] = userInfo.id;
        headers['X-Is-Login'] = '1';
      }
      
      const response = await fetch(apiConfig.endpoints.likedPosts, { headers });
      if (response.ok) {
        const data = await response.json();
        setLikedPosts(data);
      }
    } catch (error) {
      console.error('좋아요한 게시물 조회 실패:', error);
    }
  };


  const handleTabChange = async (tab: 'home' | 'search' | 'create' | 'activity' | 'profile') => {
    setActiveTab(tab);
    
    // SEO 메타 태그 업데이트
    const baseUrl = 'https://ddongstargram.com';
    const tabNames = {
      home: '홈',
      search: '검색',
      create: '게시글 작성',
      activity: '활동',
      profile: '프로필'
    };
    
    const tabDescriptions = {
      home: '똥스타그램 홈 - 재미있는 똥 사진과 일기를 확인하세요',
      search: '똥스타그램 검색 - 원하는 게시글을 찾아보세요',
      create: '똥스타그램 게시글 작성 - 새로운 똥 사진과 일기를 공유하세요',
      activity: '똥스타그램 활동 - 좋아요한 게시글을 확인하세요',
      profile: '똥스타그램 프로필 - 내 프로필을 관리하세요'
    };
    
    const currentUrl = `${baseUrl}#${tab}`;
    const title = `똥스타그램 - ${tabNames[tab]}`;
    const description = tabDescriptions[tab];
    
    // 메타 태그 업데이트
    updateTitle(title);
    updateMetaTag('description', description);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateCanonicalUrl(currentUrl);
    
    // URL 해시 업데이트
    window.history.pushState(null, '', currentUrl);
    
    // 모든 탭에서 최신 데이터 가져오기
    if (tab === 'home') {
      await fetchPosts(); // 홈 탭에서는 게시물 새로고침
    } else if (tab === 'activity') {
      await fetchLikedPosts(); // 좋아요 탭에서는 좋아요한 게시물 새로고침
    } else if (tab === 'profile') {
      await fetchUserInfo(); // 프로필 탭에서는 사용자 정보 새로고침
    }
  };

  const handlePostCreated = () => {
    fetchPosts();
    checkDailyPost();
  };

  const handleProfileUpdate = (updates: Partial<Omit<AnonymousProfile, 'id' | 'createdAt'>>) => {
    profileService.updateProfile(updates);
    const updatedProfile = profileService.getProfile();
    setAnonymousProfile(updatedProfile);
  };

  const getEffectiveUserInfo = () => {
    if (userInfo?.display_name) {
      return userInfo; // 로그인된 사용자
    }
    if (anonymousProfile) {
      return {
        id: localStorage.getItem('user_id'), // 익명 사용자 ID 추가
        encrypted_yw_id: localStorage.getItem('user_id'), // PostForm에서 사용하는 필드
        display_name: anonymousProfile.displayName,
        photo_url: null, // 익명 사용자는 photo_url 대신 로컬 아바타 사용
        anonymous_profile: anonymousProfile
      };
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Instagram 스타일 헤더 */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">💩</span>
              </div>
              <div className="flex flex-col">
                <h1 className="text-xl font-semibold text-gray-900">
                  똥스타그램
                </h1>
                {userTimezone && (
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-gray-500">🌍</span>
                    <span className="text-xs text-gray-500">{userTimezone}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button onClick={() => handleTabChange('search')}>
                <svg className={`w-6 h-6 ${activeTab === 'search' ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto bg-white min-h-screen pb-20">
        {/* 탭별 컨텐츠 */}
        {activeTab === 'home' && (
          <>
            {/* 게시물 작성 폼 */}
            <div className="border-b border-gray-100">
              <PostForm 
                userInfo={getEffectiveUserInfo()}
                anonymousProfile={anonymousProfile}
                canPost={canPost}
                onPostCreated={handlePostCreated}
              />
            </div>

            {/* 게시물 목록 */}
            <div>
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600"></div>
                </div>
              ) : (
                <PostList posts={posts} userInfo={getEffectiveUserInfo()} anonymousProfile={anonymousProfile} onPostUpdated={() => fetchPosts()} />
              )}
            </div>
          </>
        )}

        {activeTab === 'search' && (
          <SearchTab userInfo={getEffectiveUserInfo()} />
        )}

        {activeTab === 'create' && (
          <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
            <div className="p-6">
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                  <span className="text-white text-3xl">💩</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">똥 올리기</h2>
                <p className="text-gray-600 text-sm">오늘의 특별한 똥을 공유해보세요!</p>
              </div>
              
              {canPost ? (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <PostForm 
                    userInfo={getEffectiveUserInfo()}
                    anonymousProfile={anonymousProfile}
                    canPost={canPost}
                    onPostCreated={handlePostCreated}
                  />
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-400 text-2xl">😴</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">오늘은 충분히 똥을 올렸어요!</h3>
                  <p className="text-gray-500 text-sm mb-4">하루에 최대 5개까지만 올릴 수 있어요</p>
                  <div className="text-xs text-gray-400">
                    내일 새로운 똥을 공유해주세요! 🌅
                  </div>
                </div>
              )}
              
              <div className="mt-8 text-center">
                <div className="inline-flex items-center space-x-2 text-xs text-gray-400 bg-white rounded-full px-4 py-2 shadow-sm">
                  <span>💡</span>
                  <span>팁: 사진과 함께 재미있는 이야기를 적어보세요!</span>
                </div>
              </div>
            </div>
          </div>
        )}


        {activeTab === 'activity' && (
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-4">내가 좋아요한 게시물</h2>
            {likedPosts.length > 0 ? (
              <PostList posts={likedPosts} userInfo={getEffectiveUserInfo()} anonymousProfile={anonymousProfile} onPostUpdated={() => {fetchPosts(); fetchLikedPosts();}} />
            ) : (
              <div className="text-center text-gray-500 py-16">
                <div className="w-32 h-32 mx-auto mb-6 flex items-center justify-center">
                  <svg className="w-full h-full text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">아직 좋아요한 게시물이 없어요</h3>
                <p className="text-sm text-gray-500 mb-4">마음에 드는 똥을 발견하면 하트를 눌러보세요!</p>
                <div className="flex items-center justify-center space-x-2 text-xs text-gray-400">
                  <span>💩</span>
                  <span>→</span>
                  <span>❤️</span>
                </div>
              </div>
            )}
          </div>
        )}


        {activeTab === 'profile' && (
          <div className="p-4">
            <div className="text-center mb-6">
              <div className={`w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden ${
                userInfo?.photo_url ? '' : anonymousProfile ? `bg-gradient-to-br ${anonymousProfile.avatarColor}` : 'bg-gradient-to-br from-purple-400 to-pink-400'
              }`}>
                {userInfo?.photo_url ? (
                  <img src={userInfo.photo_url} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-semibold text-xl">
                    {anonymousProfile?.avatarEmoji || userInfo?.display_name?.[0] || 'U'}
                  </div>
                )}
              </div>
              <h2 className="text-lg font-semibold">{userInfo?.display_name || anonymousProfile?.displayName || '익명 사용자'}</h2>
              {!userInfo?.display_name && (
                <p className="text-gray-500 text-sm mt-1">익명 사용자</p>
              )}
            </div>
            
            {/* 프로필 수정 섹션 */}
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">프로필 정보</h3>
                  {!userInfo?.display_name && anonymousProfile && (
                    <button 
                      onClick={() => setShowProfileEditor(true)}
                      className="text-blue-500 text-sm hover:text-blue-600 transition-colors"
                    >
                      수정
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-600">닉네임</label>
                    <p className="text-gray-900">{userInfo?.display_name || anonymousProfile?.displayName || '설정되지 않음'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">로그인 상태</label>
                    <p className="text-gray-900">{userInfo?.display_name ? '로그인됨' : '익명 사용자'}</p>
                  </div>
                  {anonymousProfile && (
                    <div>
                      <label className="text-sm text-gray-600">프로필 생성일</label>
                      <p className="text-gray-900">{new Date(anonymousProfile.createdAt).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">프로필 수정 안내</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      {userInfo?.display_name 
                        ? '로그인한 사용자는 Youware 플랫폼에서 프로필을 수정할 수 있습니다.' 
                        : '익명 사용자도 닉네임과 아바타를 자유롭게 수정할 수 있습니다. 수정 버튼을 눌러보세요!'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {!userInfo?.display_name && (
                <button
                  onClick={() => {
                    profileService.clearProfile();
                    initializeAnonymousProfile();
                  }}
                  className="w-full py-2 text-gray-600 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  새 랜덤 프로필 생성
                </button>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Instagram 스타일 하단 네비게이션 */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="max-w-md mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <button onClick={() => handleTabChange('home')}>
              <svg className={`w-6 h-6 ${activeTab === 'home' ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
            <button onClick={() => handleTabChange('search')}>
              <svg className={`w-6 h-6 ${activeTab === 'search' ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <button onClick={() => handleTabChange('create')}>
              <div className={`w-6 h-6 border-2 ${activeTab === 'create' ? 'border-gray-900' : 'border-gray-400'} rounded-sm`}>
                <svg className={`w-full h-full ${activeTab === 'create' ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
            </button>
            <button onClick={() => handleTabChange('activity')}>
              <svg className={`w-6 h-6 ${activeTab === 'activity' ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
            <button onClick={() => handleTabChange('profile')}>
              <div className={`w-8 h-8 rounded-full ${activeTab === 'profile' ? 'ring-2 ring-gray-900' : ''} overflow-hidden ${
                userInfo?.photo_url ? '' : anonymousProfile ? `bg-gradient-to-br ${anonymousProfile.avatarColor}` : 'bg-gradient-to-br from-purple-400 to-pink-400'
              }`}>
                {userInfo?.photo_url ? (
                  <img src={userInfo.photo_url} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-semibold text-sm">
                    {anonymousProfile?.avatarEmoji || userInfo?.display_name?.[0] || 'U'}
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* 프로필 수정 모달 */}
      {showProfileEditor && anonymousProfile && (
        <ProfileEditor
          profile={anonymousProfile}
          onUpdate={handleProfileUpdate}
          onClose={() => setShowProfileEditor(false)}
        />
      )}
    </div>
  );
}

export default App;